import type { Num, SeriesResponse } from "../types";
import { CANON } from "../lib/settings";
import { fmt } from "../lib/format";
import { Labeled } from "./Hint";
import { HINTS } from "../lib/hints";

const last = (x: Num[]) => {
  for (let i = x.length - 1; i >= 0; i--) if (x[i] != null) return x[i];
  return null;
};

/** Тексты ситуаций M9 (гл. 9) для пары «умные деньги / публика». */
export function driverText(code: number, smart: string, pub: string): { text: string; dir: number } | null {
  switch (code) {
    case 1:
      return { dir: -1, text: `Интерес растёт вместе с ценой, а ${smart} в нетто продают — новые лонги в сумме набрали ${pub}. По книге так выглядит вершина.` };
    case 2:
      return { dir: 1, text: `Интерес растёт на падении цены, а ${smart} покупают — пришли сильные деньги. Так выглядит дно.` };
    case 3:
      return { dir: 1, text: `Интерес и цена падают, а ${smart} наращивают лонги — новые покупки только от «умных». Бычий признак.` };
    case 4:
      return { dir: 1, text: `Толпа уходит (интерес падает или низкий), а ${smart} покупают — ралли близко.` };
    case 5:
      return { dir: -1, text: `Цена и интерес растут, а ${smart} наращивают шорты на росте — сетап на продажу.` };
    case 6:
      return { dir: -1, text: `Интерес растёт: в нетто покупают ${pub}, а ${smart} в нетто продают им — открывают шортов больше, чем лонгов. По книге (гл. 9) — вероятен слом тренда.` };
    default:
      return null;
  }
}

function share(part: Num, total: Num) {
  if (part == null || total == null || total === 0) return null;
  const sh = (part / total) * 100;
  return sh >= 0 && sh <= 100 ? Math.round(sh) : null;
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** M9: кто изменил открытый интерес за последние k баров. */
export function OiDriverCard({ d, smartGen, publicGen }: { d: SeriesResponse; smartGen: string; publicGen: string }) {
  const k = d.oi.changeBars;
  const weeks = d.timeframe === "W" ? k : Math.round(k / 5);
  const dOi = last(d.oi.dTotal);
  const total = last(d.oi.total);
  const sl = last(d.oi.dSmartLong);
  const pl = last(d.oi.dPublicLong);
  const ss = last(d.oi.dSmartShort);
  const ps = last(d.oi.dPublicShort);
  const pct = dOi != null && total != null && total - dOi !== 0 ? (dOi / (total - dOi)) * 100 : null;
  const sit = driverText(d.summary.driver, smartGen, publicGen);
  const netK = sl != null && ss != null ? sl - ss : null;
  const trend = d.summary.trend;
  const cot = d.summary.smartCotShort;
  const against = sit != null && trend !== 0 && trend !== sit.dir;
  // Сетап по уровню (COT у края) против движения за k недель.
  let context: string | null = null;
  if (sit && cot != null) {
    if (sit.dir > 0 && cot <= 20) {
      context = `${cap(smartGen)} всё ещё в шорте у дна полугодового диапазона (COT ${fmt.idx(cot)}), но сокращают его. Продажи ещё в силе, но открывать новые поздно; покупать — рано.`;
    } else if (sit.dir < 0 && cot >= 80) {
      context = `${cap(smartGen)} всё ещё в лонге у верха полугодового диапазона (COT ${fmt.idx(cot)}), но сокращают его. Покупки ещё в силе, но открывать новые поздно; продавать — рано.`;
    } else if ((sit.dir > 0 && cot >= 80) || (sit.dir < 0 && cot <= 20)) {
      context = `Совпадает с положением ${smartGen === "юрлица" ? "юрлиц" : "физлиц"} (COT ${fmt.idx(cot)}) — подтверждение сетапа.`;
    }
  }
  const side = (label: string, smartV: Num, pubV: Num) => {
    const a = share(smartV, dOi);
    const b = share(pubV, dOi);
    return (
      <li>
        <span className="side-label">{label}</span>
        <span>
          <i className="dot smart" /> {smartGen} {fmt.signed(smartV)}
          {a != null && <em> ({a}%)</em>}
        </span>
        <span>
          <i className="dot crowd" /> {publicGen} {fmt.signed(pubV)}
          {b != null && <em> ({b}%)</em>}
        </span>
      </li>
    );
  };
  return (
    <section className="panel extra">
      <h2>
        <Labeled hint={HINTS.driver}>Кто меняет интерес</Labeled>
      </h2>
      {dOi == null ? (
        <p className="muted">Мало истории: нужно хотя бы {weeks} недели.</p>
      ) : (
        <>
          <p className="big-line">
            За {weeks} нед. интерес{" "}
            <b className={dOi >= 0 ? "up" : "down"}>
              {fmt.signed(dOi)} контр.{pct != null ? ` (${fmt.signedPct(pct)})` : ""}
            </b>
          </p>
          <ul className="sides">
            {side("Прирост лонгов", sl, pl)}
            {side("Прирост шортов", ss, ps)}
          </ul>
          <p className="muted note">
            Каждый новый лонг открывается против нового шорта, поэтому прирост лонгов сам по себе не показывает, кто
            покупает. Направление группы — её нетто: прирост лонгов минус прирост шортов.
          </p>
          {netK != null && (
            <p className="net-line">
              Нетто {smartGen === "юрлица" ? "юрлиц" : "физлиц"} за {weeks} нед.:{" "}
              <b className={netK >= 0 ? "up" : "down"}>{fmt.signed(netK)}</b>{" "}
              <span className="muted">— {netK > 0 ? "покупают" : netK < 0 ? "продают" : "без изменений"}</span>
            </p>
          )}
          <div className={`verdict ${sit ? (against ? "against" : sit.dir > 0 ? "up" : "down") : "muted"}`}>
            <p>{sit ? sit.text : "Ни одна из шести ситуаций книги сейчас не выполняется."}</p>
            {against && (
              <p className="verdict-note">
                Против тренда: средняя цены {trend > 0 ? "растёт" : "падает"}, а признак на {sit!.dir > 0 ? "покупку" : "продажу"}.
                По методике такое пока только наблюдают.
              </p>
            )}
            {context && <p className="verdict-note">{context}</p>}
          </div>
        </>
      )}
    </section>
  );
}

const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** M11: сезонный индекс — типичный год по неделям. */
export function SeasonalCard({ d }: { d: SeriesResponse }) {
  const z = d.seasonal;
  if (!z) {
    return (
      <section className="panel extra">
        <h2>
          <Labeled hint={HINTS.seasonal}>Сезонность</Labeled>
        </h2>
        <p className="muted">Нет истории цен.</p>
      </section>
    );
  }
  const vals = z.smooth.map((v) => v ?? 0);
  const raw = z.seas.map((v) => v ?? 0);
  const all = [...vals, ...raw];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const W = 520;
  const H = 150;
  const pad = 8;
  const x = (w: number) => pad + (w / 51) * (W - 2 * pad);
  const y = (v: number) => (hi === lo ? H / 2 : pad + (1 - (v - lo) / (hi - lo)) * (H - 2 * pad));
  const path = (a: number[]) => a.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const cw = z.currentWeek - 1;
  const ahead = Math.min(51, cw + z.horizonWeeks);
  const fewYears = z.years < 10;
  return (
    <section className="panel extra">
      <h2>
        <Labeled hint={HINTS.seasonal}>Сезонность</Labeled>
      </h2>
      <p className="big-line">
        Ближайшие {z.horizonWeeks} нед.:{" "}
        <b className={z.bias > 0 ? "up" : z.bias < 0 ? "down" : "muted"}>
          {z.bias > 0 ? "обычно рост" : z.bias < 0 ? "обычно снижение" : "без выраженной склонности"}
        </b>
      </p>
      <svg viewBox={`0 0 ${W} ${H + 22}`} className="season-svg" role="img" aria-label="Сезонная кривая по неделям года">
        <rect x={x(cw)} y={0} width={Math.max(2, x(ahead) - x(cw))} height={H} className="season-now" />
        {MONTHS.map((m, i) => (
          <g key={m}>
            <line x1={x((i * 52) / 12)} x2={x((i * 52) / 12)} y1={0} y2={H} className="grid-v" />
            <text x={x((i * 52) / 12) + 3} y={H + 15} className="tick">
              {m}
            </text>
          </g>
        ))}
        {lo < 0 && hi > 0 && <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} className="zero" />}
        <path d={path(raw)} className="season-raw" />
        <path d={path(vals)} className="season-line" />
        <circle cx={x(cw)} cy={y(vals[cw])} r={4} className="season-dot" />
      </svg>
      <p className="muted small">
        Средняя по {z.years} {z.years === 1 ? "году" : "годам"} истории цен ({fmt.date(z.from)} — {fmt.date(z.to)}), неделя
        сейчас — {z.currentWeek}. Толстая линия — сглаженная, тонкая — как есть.
        {fewYears && " Лет меньше десяти — кривая шумная, используйте как подтверждение, а не как сигнал."}
      </p>
    </section>
  );
}

/** M16: сделки по входам M14-C со стопами гл. 12. */
export function PositionsCard({ d }: { d: SeriesResponse }) {
  const p = d.positions;
  const trades = [...p.trades].reverse().slice(0, 8);
  const units = p.pointValue != null ? "руб." : "пунктов";
  const total = p.trades.reduce((a, t) => a + (p.pointValue != null ? (t.rub ?? 0) : t.points), 0);
  const wins = p.trades.filter((t) => t.points > 0).length;
  const prof = CANON;
  return (
    <section className="panel extra">
      <h2>
        <Labeled hint={HINTS.positions16}>Позиция и стопы</Labeled>
      </h2>
      {p.open ? (
        <p className="big-line">
          Открыта <b className={p.open.dir > 0 ? "up" : "down"}>{p.open.dir > 0 ? "покупка" : "продажа"}</b> с{" "}
          {fmt.date(p.open.entryDate)} по {fmt.price(p.open.entryPrice)}
          {p.open.reentry ? " (перевход)" : ""}; сейчас {fmt.signed(p.open.points)} пунктов
        </p>
      ) : (
        <p className="big-line muted">Открытой позиции нет.</p>
      )}
      {d.params.entryProfile === "LW_CANONICAL" ? (
      <p className="muted small">
        Правила — {prof.title} (гл. 10–13). Начальный стоп — минимум (максимум) 17 торговых дней до входа без
        inside-дней (гл. 12); в крупном тренде стоп становится минимумом (максимумом) {prof.trailDays} дней без inside-дней. Закрыто сделок: {p.trades.length}, прибыльных {wins}, итог {fmt.signed(total)} {units} (без комиссий и проскальзывания).
      </p>
      ) : (
        <p className="muted small">
          Правила — «Сетап 4+ · 3 ATR»: вход на открытии первого дня после недели, в которую балл сетапа впервые стал 4
          или 5; сетап в обратную сторону — разворот. Начальный стоп — 3 ATR(20), в крупном тренде он подтягивается к
          минимуму (максимуму) {prof.trailDays} дней без inside-дней. Закрыто сделок: {p.trades.length}, прибыльных {wins},
          итог {fmt.signed(total)} {units} (без комиссий и проскальзывания).
        </p>
      )}
      {trades.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text">Вход</th>
                <th className="text">Сторона</th>
                <th>Цена</th>
                <th className="text">Выход</th>
                <th>Цена</th>
                <th className="text">Причина</th>
                <th>Итог</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => (
                <tr key={i}>
                  <td className="text">{fmt.date(t.entryDate)}</td>
                  <td className="text">{t.dir > 0 ? "покупка" : "продажа"}{t.reentry ? ", перевход" : ""}</td>
                  <td>{fmt.price(t.entryPrice)}</td>
                  <td className="text">{fmt.date(t.exitDate)}</td>
                  <td>{fmt.price(t.exitPrice)}</td>
                  <td className="text">{t.reason}</td>
                  <td className={t.points >= 0 ? "up" : "down"}>
                    {p.pointValue != null && t.rub != null ? `${fmt.signed(t.rub)} руб.` : fmt.signed(t.points)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
